import type { ConfigType } from 'dayjs';
import { includes } from 'lodash-es';

import dayjs from '../date';
import Calendar from './Calendar';
import Inspector, { FIRST_WEEK_INDEX } from './Inspector';
import type { WeekRange } from './Inspector';

export type ToISOWeekOfMonth = { year: number; month: number; week: number };

/**
 * @see {@link https://en.wikipedia.org/wiki/ISO_week_date ISO 8601}
 */
export default class WeekOfMonth {
  private readonly time: ReturnType<typeof dayjs>;
  private readonly monthMatrix: WeekRange[];
  private readonly lastWeek: number;

  constructor(time: ConfigType) {
    this.time = dayjs(time);
    const calendar = new Calendar(this.time);
    this.monthMatrix = calendar.getMonthMatrix();
    this.lastWeek = calendar.getLastWeekIndex();
  }

  private getLastMonth() {
    return this.time.subtract(1, 'month').endOf('month');
  }

  private getNextMonth() {
    return this.time.add(1, 'month').startOf('month');
  }

  private getWeekIndex() {
    return this.monthMatrix.findIndex((monthRange) => includes(monthRange, this.time.date()));
  }

  private makeReturnObject(year: number, month: number, weekIndex: number) {
    return {
      year,
      month,
      week: weekIndex + 1,
    };
  }

  toISOWeekOfMonth(): ToISOWeekOfMonth {
    const year = this.time.get('year');
    const month = this.time.get('month') + 1;
    const weekIndex = this.getWeekIndex();
    const presentWeekChunk = this.monthMatrix[weekIndex];

    const isPresentWeekOfLastMonth = Inspector.isWeekOfLastMonth(
      this.monthMatrix[FIRST_WEEK_INDEX],
    );

    if (Inspector.isFirstWeek(weekIndex)) {
      return isPresentWeekOfLastMonth
        ? new WeekOfMonth(this.getLastMonth()).toISOWeekOfMonth()
        : this.makeReturnObject(year, month, weekIndex);
    }

    const isLastWeek = weekIndex === this.lastWeek;

    if (isLastWeek) {
      return Inspector.isWeekOfNextMonth(presentWeekChunk)
        ? new WeekOfMonth(this.getNextMonth()).toISOWeekOfMonth()
        : this.makeReturnObject(year, month, weekIndex);
    }

    const presentWeek = isPresentWeekOfLastMonth ? weekIndex - 1 : weekIndex;

    return this.makeReturnObject(year, month, presentWeek);
  }
}
